import { apiService } from './api';

export interface PurchaseItemDTO {
  purchaseItemId: number;
  productId: number;
  productSKU: string;
  productName: string;
  supplierId: number;
  supplierName: string;
  supplierItemCode: string;
  purchasePrice: number;
  currency: string;
  minimumOrderQuantity: number;
  leadTimeDays: number;
  description?: string;
  isActive: boolean;
  createdDate: string;
  taxId?: number;
  taxCode?: string;
  taxName?: string;
  taxPercentage?: number;
}

export interface CreatePurchaseItemRequest {
  productId: number;
  supplierId: number;
  supplierItemCode: string;
  purchasePrice: number;
  currency: string;
  minimumOrderQuantity: number;
  leadTimeDays: number;
  description?: string;
  taxId?: number;
}

export interface UpdatePurchaseItemRequest {
  supplierId: number;
  supplierItemCode: string;
  purchasePrice: number;
  currency: string;
  minimumOrderQuantity: number;
  leadTimeDays: number;
  description?: string;
  isActive: boolean;
  taxId?: number;
}

/** One supplier pricing row in a bulk upsert request. */
export interface SupplierPricingRowRequest {
  /** Omit for new rows; set to the existing purchaseItemId when updating. */
  purchaseItemId?: number;
  supplierId: number;
  supplierItemCode: string;
  purchasePrice: number;
  currency: string;
  minimumOrderQuantity: number;
  leadTimeDays: number;
  description?: string;
  taxId?: number;
}

/** Payload for PUT /purchase-items/product/{productId}/suppliers */
export interface UpsertProductSuppliersRequest {
  suppliers: SupplierPricingRowRequest[];
}

const PURCHASE_ITEMS_API = '/purchase-items';

export const purchaseItemService = {
  getPurchaseItems: (pageNumber = 1, pageSize = 50) =>
    apiService.get<any>(PURCHASE_ITEMS_API, { pageNumber, pageSize }),

  getPurchaseItemById: (id: number) =>
    apiService.get<PurchaseItemDTO>(`${PURCHASE_ITEMS_API}/${id}`),

  getPurchaseItemsByProduct: (productId: number) =>
    apiService.get<PurchaseItemDTO[]>(`${PURCHASE_ITEMS_API}/product/${productId}`),

  getPurchaseItemsBySupplier: (supplierId: number) =>
    apiService.get<PurchaseItemDTO[]>(`${PURCHASE_ITEMS_API}/supplier/${supplierId}`),

  createPurchaseItem: (data: CreatePurchaseItemRequest) =>
    apiService.post<PurchaseItemDTO>(PURCHASE_ITEMS_API, data),

  updatePurchaseItem: (id: number, data: UpdatePurchaseItemRequest) =>
    apiService.put<PurchaseItemDTO>(`${PURCHASE_ITEMS_API}/${id}`, data),

  /**
   * Atomically replaces all active supplier pricing rows for a product.
   * New rows are created, existing rows are updated, omitted active rows are soft-deleted.
   */
  upsertProductSuppliers: (productId: number, data: UpsertProductSuppliersRequest) =>
    apiService.put<PurchaseItemDTO[]>(`${PURCHASE_ITEMS_API}/product/${productId}/suppliers`, data),

  deletePurchaseItem: (id: number) =>
    apiService.delete<void>(`${PURCHASE_ITEMS_API}/${id}`),
};
